

import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import toast from 'react-hot-toast';
import api from '../utils/api';
import Sidebar from '../components/layout/Sidebar';
import SubcategoryPanel from '../components/category/SubcategoryPanel';
import Modal from '../components/common/Modal';
import ConfirmDialog from '../components/common/ConfirmDialog';

export default function DashboardPage() {
  const { user, logout, isAdmin } = useAuth();

  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [categoryData, setCategoryData] = useState(null);
  const [loadingCats, setLoadingCats] = useState(true);
  const [loadingCatData, setLoadingCatData] = useState(false);

  const [catSearch, setCatSearch] = useState('');
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const [showAddSub, setShowAddSub] = useState(false);
  const [showEditSub, setShowEditSub] = useState(null);
  const [showDeleteSub, setShowDeleteSub] = useState(null);
  const [showAddCat, setShowAddCat] = useState(false);
  const [showEditCat, setShowEditCat] = useState(null);
  const [showDeleteCat, setShowDeleteCat] = useState(null);

  const fetchCategories = useCallback(async () => {
    try {
      const res = await api.get('/categories');
      setCategories(res.data.categories);
      if (!selectedCategory && res.data.categories.length > 0) {
        setSelectedCategory(res.data.categories[0]);
      }
    } catch (err) {
      toast.error('Failed to load categories');
    } finally {
      setLoadingCats(false);
    }
  }, [selectedCategory]);

  useEffect(() => { 
    fetchCategories(); 
  }, []);

  useEffect(() => {
    if (!selectedCategory) return;
    setLoadingCatData(true);
    api.get(`/categories/${selectedCategory.slug}`)
      .then(res => { 
        setCategoryData(res.data); 
        setLoadingCatData(false);
      })
      .catch((err) => { 
        console.error('Failed to load category data:', err);
        toast.error('Failed to load category data'); 
        setLoadingCatData(false);
      });
  }, [selectedCategory]);

  const refreshCatData = useCallback(async () => {
    if (!selectedCategory) return;
    try {
      const res = await api.get(`/categories/${selectedCategory.slug}`);
      setCategoryData(res.data);
      return res.data;
    } catch (err) {
      console.error('Refresh failed:', err);
      throw err;
    }
  }, [selectedCategory]);

  const handleAddCategory = async (name) => {
    try {
      const res = await api.post('/categories', { name });
      await fetchCategories();
      setShowAddCat(false);
      toast.success(`Category "${name}" created`);
      setSelectedCategory(res.data.category);
    } catch (err) {
      throw new Error(err.response?.data?.message || 'Failed to create category');
    }
  };

  const handleEditCategory = async (name) => {
    try {
      await api.put(`/categories/${showEditCat.id}`, { name });
      await fetchCategories();
      setShowEditCat(null);
      toast.success('Category updated');
    } catch (err) {
      throw new Error(err.response?.data?.message || 'Failed to update category');
    }
  };

  const handleDeleteCategory = async () => {
    try {
      await api.delete(`/categories/${showDeleteCat.id}`);
      const wasSelected = selectedCategory?.id === showDeleteCat.id;
      await fetchCategories();
      setShowDeleteCat(null);
      toast.success('Category deleted');
      if (wasSelected) setSelectedCategory(null);
    } catch (err) {
      toast.error('Failed to delete category');
    }
  };

  const handleAddSubcategory = async (data) => {
    try {
      await api.post(`/categories/${selectedCategory.slug}/subcategories`, data);
      await refreshCatData();
      setShowAddSub(false);
      toast.success(`Site "${data.name}" created successfully`);
    } catch (err) {
      console.error('Error creating site:', err);
      throw new Error(err.response?.data?.message || 'Failed to create site');
    }
  };

  const handleEditSubcategory = async (data) => {
    try {
      await api.put(`/categories/${selectedCategory.slug}/subcategories/${showEditSub.id}`, data);
      await refreshCatData();
      setShowEditSub(null);
      toast.success('Site updated successfully');
    } catch (err) {
      console.error('Error updating site:', err);
      throw new Error(err.response?.data?.message || 'Failed to update site');
    }
  };

  const handleDeleteSubcategory = async () => {
    try {
      await api.delete(`/categories/${selectedCategory.slug}/subcategories/${showDeleteSub.id}`);
      await refreshCatData();
      setShowDeleteSub(null);
      toast.success('Site deleted successfully');
    } catch (err) {
      toast.error('Failed to delete site');
    }
  };

  const filteredCategories = categories.filter(c =>
    c.name.toLowerCase().includes(catSearch.toLowerCase())
  );

  return (
    <div className="min-h-screen flex flex-col bg-surface-50">
      <header className="h-14 bg-white border-b border-surface-200 flex items-center px-4 gap-4 z-30 sticky top-0">
        <button onClick={() => setSidebarOpen(v => !v)} className="lg:hidden btn-ghost p-2">
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
          </svg>
        </button>
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 bg-primary-600 rounded-lg flex items-center justify-center">
            <svg viewBox="0 0 24 24" fill="none" className="w-4 h-4 text-white">
              <path d="M15 7a4 4 0 014 4v1a4 4 0 01-4 4h-1l-1 2H8l-1-2H6a4 4 0 01-4-4v-1a4 4 0 014-4h9z" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round"/>
              <circle cx="9" cy="12" r="1" fill="currentColor"/>
              <circle cx="12" cy="12" r="1" fill="currentColor"/>
              <circle cx="15" cy="12" r="1" fill="currentColor"/>
            </svg>
          </div>
          <span className="font-display text-lg font-bold text-surface-900">GenKey</span>
        </div>
        <div className="ml-auto flex items-center gap-3">
          <div className="hidden sm:flex items-center gap-2 text-sm">
            <div className="w-7 h-7 rounded-full bg-primary-100 text-primary-700 flex items-center justify-center font-semibold text-xs">
              {user?.name?.charAt(0).toUpperCase()}
            </div>
            <span className="text-surface-700 font-medium">{user?.name}</span>
            <span className={isAdmin ? 'badge-admin' : 'badge-user'}>{isAdmin ? 'Admin' : 'User'}</span>
          </div>
          <button onClick={logout} className="btn-ghost text-red-500 hover:text-red-700 hover:bg-red-50">
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
            </svg>
            <span className="hidden sm:inline">Logout</span>
          </button>
        </div>
      </header>

      <div className="flex flex-1 overflow-hidden">
        <Sidebar
          categories={filteredCategories}
          selectedCategory={selectedCategory}
          onSelectCategory={(cat) => { setSelectedCategory(cat); setSidebarOpen(false); }}
          onAddCategory={() => setShowAddCat(true)}
          onEditCategory={(cat) => setShowEditCat(cat)}
          onDeleteCategory={(cat) => setShowDeleteCat(cat)}
          isAdmin={isAdmin}
          loading={loadingCats}
          search={catSearch}
          onSearch={setCatSearch}
          mobileOpen={sidebarOpen}
          onMobileClose={() => setSidebarOpen(false)}
        />

        <main className="flex-1 overflow-y-auto p-4 lg:p-6">
          {!selectedCategory ? (
            <EmptyState message="Select a category from the sidebar" />
          ) : loadingCatData ? (
            <div className="flex items-center justify-center h-48">
              <div className="w-8 h-8 border-2 border-primary-600 border-t-transparent rounded-full animate-spin" />
            </div>
          ) : (
            <>
              <div className="flex items-center gap-2 mb-5 text-sm text-surface-500">
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
                </svg>
                <span className="font-medium text-surface-900">{selectedCategory.name}</span>
              </div>

              <SubcategoryPanel
                subcategories={categoryData?.subcategories || []}
                onAddSub={() => setShowAddSub(true)}
                onEditSub={(sub) => setShowEditSub(sub)}
                onDeleteSub={(sub) => setShowDeleteSub(sub)}
                isAdmin={isAdmin}
              />
            </>
          )}
        </main>
      </div>

      {showAddCat && <NameModal title="Add Category" onSave={handleAddCategory} onClose={() => setShowAddCat(false)} />}
      {showEditCat && <NameModal title="Edit Category" initialValue={showEditCat.name} onSave={handleEditCategory} onClose={() => setShowEditCat(null)} />}
      {showDeleteCat && <ConfirmDialog message={`Delete category "${showDeleteCat.name}" and all its data?`} onConfirm={handleDeleteCategory} onCancel={() => setShowDeleteCat(null)} dangerous />}

      {showAddSub && <SubcategoryModal title="Add Site" onSave={handleAddSubcategory} onClose={() => setShowAddSub(false)} />}
      {showEditSub && <SubcategoryModal title="Edit Site" initialData={showEditSub} onSave={handleEditSubcategory} onClose={() => setShowEditSub(null)} />}
      {showDeleteSub && <ConfirmDialog message={`Delete site "${showDeleteSub.name}" and all its data?`} onConfirm={handleDeleteSubcategory} onCancel={() => setShowDeleteSub(null)} dangerous />}
    </div>
  );
}

function EmptyState({ message }) {
  return (
    <div className="flex flex-col items-center justify-center h-64 text-surface-400">
      <svg className="w-14 h-14 mb-3 opacity-30" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M9 13h6m-3-3v6m5 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
      </svg>
      <p className="text-sm">{message}</p>
    </div>
  );
}

function NameModal({ title, initialValue = '', onSave, onClose }) {
  const [value, setValue] = useState(initialValue);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!value.trim()) { setError('Name is required.'); return; }
    setLoading(true);
    try { await onSave(value.trim()); }
    catch (err) { setError(err.message); }
    finally { setLoading(false); }
  };

  return (
    <Modal title={title} onClose={onClose}>
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="block text-xs font-semibold text-surface-600 mb-1.5 uppercase tracking-wide">Name</label>
          <input autoFocus className="input-field" value={value} onChange={e => { setValue(e.target.value); setError(''); }} placeholder="Enter name…" />
          {error && <p className="text-xs text-red-600 mt-1">{error}</p>}
        </div>
        <div className="flex justify-end gap-2 pt-1">
          <button type="button" onClick={onClose} className="btn-secondary">Cancel</button>
          <button type="submit" disabled={loading} className="btn-primary">{loading ? 'Saving…' : 'Save'}</button>
        </div>
      </form>
    </Modal>
  );
}

function SubcategoryModal({ title, initialData = null, onSave, onClose }) {
  const [form, setForm] = useState({
    name: initialData?.name || '',
    url: initialData?.url || '',
    username: initialData?.username || '',
    password: initialData?.password || '',
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [showPass, setShowPass] = useState(false);

  const validate = () => {
    const errs = {};
    if (!form.name.trim()) errs.name = 'Site name is required';
    if (!form.username.trim()) errs.username = 'Username is required';
    if (!form.password) errs.password = 'Password is required';
    return errs;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length) { 
      setErrors(errs); 
      return; 
    }
    
    setLoading(true);
    try { 
      await onSave(form); 
    } catch (err) { 
      setErrors({ general: err.message }); 
    } finally { 
      setLoading(false);
    }
  };

  // Remove https:// for display in URL field
  const displayUrl = form.url.replace(/^https?:\/\//, '');

  return (
    <Modal title={title} onClose={onClose} wide>
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="block text-xs font-semibold text-surface-600 mb-1.5 uppercase tracking-wide">
            Site Name *
          </label>
          <input 
            autoFocus 
            className={`input-field ${errors.name ? 'border-red-400' : ''}`} 
            value={form.name}
            onChange={e => { setForm(f => ({ ...f, name: e.target.value })); setErrors(p => ({ ...p, name: '' })); }}
            placeholder="e.g., Google Account" 
          />
          {errors.name && <p className="text-xs text-red-600 mt-1">{errors.name}</p>}
        </div>
        
        <div>
          <label className="block text-xs font-semibold text-surface-600 mb-1.5 uppercase tracking-wide">
            URL
          </label>
          <input 
            className="input-field" 
            value={displayUrl}
            onChange={e => setForm(f => ({ ...f, url: e.target.value }))}
            placeholder="example.com (https:// added automatically)" 
          />
          <p className="text-xs text-surface-400 mt-1">Just type the domain name, https:// will be added automatically</p>
        </div>
        
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-semibold text-surface-600 mb-1.5 uppercase tracking-wide">
              Username *
            </label>
            <input 
              className={`input-field ${errors.username ? 'border-red-400' : ''}`} 
              value={form.username}
              onChange={e => { setForm(f => ({ ...f, username: e.target.value })); setErrors(p => ({ ...p, username: '' })); }}
              placeholder="admin@example.com" 
            />
            {errors.username && <p className="text-xs text-red-600 mt-1">{errors.username}</p>}
          </div>
          
          <div>
            <label className="block text-xs font-semibold text-surface-600 mb-1.5 uppercase tracking-wide">
              Password *
            </label>
            <div className="relative">
              <input 
                type={showPass ? 'text' : 'password'} 
                className={`input-field pr-10 ${errors.password ? 'border-red-400' : ''}`}
                value={form.password}
                onChange={e => { setForm(f => ({ ...f, password: e.target.value })); setErrors(p => ({ ...p, password: '' })); }}
                placeholder="••••••••" 
              />
              <button 
                type="button" 
                onClick={() => setShowPass(v => !v)} 
                className="absolute right-3 top-1/2 -translate-y-1/2 text-surface-400 hover:text-surface-600"
              >
                {showPass ? (
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                  </svg>
                ) : (
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                  </svg>
                )}
              </button>
            </div>
            {errors.password && <p className="text-xs text-red-600 mt-1">{errors.password}</p>}
          </div>
        </div>
        
        {errors.general && <p className="text-xs text-red-600">{errors.general}</p>}
        
        <div className="flex justify-end gap-2 pt-1">
          <button type="button" onClick={onClose} className="btn-secondary">Cancel</button>
          <button type="submit" disabled={loading} className="btn-primary">
            {loading ? 'Saving…' : title}
          </button>
        </div>
      </form>
    </Modal>
  );
}